import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const COLORS = ['#e91e63', '#9c27b0', '#3f51b5', '#2196f3', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#ffeb3b', '#ff9800'];

const AdminAnalytics = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [popularLocations, setPopularLocations] = useState({ pickups: [], dropoffs: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch revenue analytics
      const revenueResponse = await api.get('/admin/analytics/revenue?days=30');
      setRevenueData(revenueResponse.data.analytics);

      // Fetch popular locations
      const locationsResponse = await api.get('/admin/analytics/popular-locations?limit=10');
      setPopularLocations({
        pickups: locationsResponse.data.popularPickups,
        dropoffs: locationsResponse.data.popularDropoffs
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1">Detailed insights into your platform</p>
        </div>

        {/* Revenue Trend */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Daily Revenue (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#e91e63" name="Revenue ($)" />
              <Bar dataKey="rides" fill="#9c27b0" name="Rides" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Popular Locations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Popular Pickups */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Popular Pickup Locations</h2>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={popularLocations.pickups}
                  dataKey="count"
                  nameKey="location"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={(entry) => entry.location.length > 20 ? entry.location.substring(0, 20) + '...' : entry.location}
                >
                  {popularLocations.pickups.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Popular Dropoffs */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Popular Dropoff Locations</h2>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={popularLocations.dropoffs}
                  dataKey="count"
                  nameKey="location"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={(entry) => entry.location.length > 20 ? entry.location.substring(0, 20) + '...' : entry.location}
                >
                  {popularLocations.dropoffs.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Location Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Top Pickup Locations</h3>
            <div className="space-y-2">
              {popularLocations.pickups.map((location, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{location.location}</span>
                  </div>
                  <span className="text-gray-600 font-semibold">{location.count} rides</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Top Dropoff Locations</h3>
            <div className="space-y-2">
              {popularLocations.dropoffs.map((location, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{location.location}</span>
                  </div>
                  <span className="text-gray-600 font-semibold">{location.count} rides</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
